import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError, API } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { toast } from "@/lib/toast";
import {
  Download, Loader2, CheckCircle2, RotateCcw, UserCheck, Mail, ChevronDown, FileText, ShieldAlert, ArrowLeft,
} from "lucide-react";
import { convTheme, convLabels, resolveConvType } from "@/lib/conversationTheme";
import { ConversationHeader, SharesNotice, InternalNotesRail } from "@/components/conversation/ConversationChrome";

function fmtTs(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
function initials(email) { return ((email || "?").split("@")[0].slice(0, 2) || "?").toUpperCase(); }

// INBOUND external message — full-bleed, solid reserved-colour banner (purple member / blue business).
function InboundMessage({ m, theme }) {
  return (
    <div data-testid={`mailbox-msg-external-${m.message_id}`} className="-mx-5"
      style={{ borderTop: `2px solid ${theme.headerBg}`, borderBottom: `2px solid ${theme.headerBg}`, marginTop: 14, marginBottom: 14 }}>
      <div className="flex items-center gap-3 flex-wrap" style={{ background: theme.headerBg, padding: "6px 20px" }}>
        <span style={{ color: theme.headerText, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 9, letterSpacing: "0.2em" }}>EXTERNAL · INBOUND</span>
        <span style={{ color: theme.headerText, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 9, letterSpacing: "0.2em" }}>TO {(m.to_email || "").toUpperCase()}</span>
        <span className="ml-auto" style={{ color: theme.headerText, opacity: 0.8, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 9, letterSpacing: "0.2em" }}>{fmtTs(m.created_at)}</span>
      </div>
      <div style={{ background: `rgba(${theme.rgb},0.16)`, padding: "14px 20px" }}>
        <div className="flex items-start gap-3">
          <div className="shrink-0 flex items-center justify-center font-mono text-xs"
            style={{ width: 34, height: 34, background: theme.avatarBg, border: `1px solid ${theme.avatarBorder}`, color: theme.avatarInitials }}>
            {initials(m.from_email)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-xs break-all" style={{ color: theme.senderName }}>{m.from_email}</p>
            <p className="text-base leading-relaxed whitespace-pre-wrap break-words mt-1.5 font-body" style={{ color: theme.body }}>{m.body}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// OUTBOUND draft — quieter, low-opacity reserved colour, no solid fill. Always DRAFT — NOT SENT.
function OutboundMessage({ m, theme }) {
  return (
    <div data-testid={`mailbox-msg-outbound-${m.message_id}`} className="-mx-5" style={{ marginTop: 14, marginBottom: 14 }}>
      <div className="flex items-center gap-3 flex-wrap"
        style={{ background: `rgba(${theme.rgb},0.14)`, borderTop: `1px solid rgba(${theme.rgb},0.5)`, padding: "6px 20px" }}>
        <span style={{ color: theme.avatarInitials, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 9, letterSpacing: "0.2em" }}>OUTBOUND · AS {(m.from_email || "").toUpperCase()}</span>
        {m.admin_username && <span style={{ color: "#8B8D90", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 9, letterSpacing: "0.2em" }}>WRITTEN BY @{m.admin_username.toUpperCase()}</span>}
        <span data-testid={`mailbox-draft-marker-${m.message_id}`} className="ml-auto inline-flex items-center gap-1"
          style={{ color: "#EBB44A", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 9, letterSpacing: "0.2em" }}>
          <FileText className="w-3 h-3" /> DRAFT — NOT SENT
        </span>
      </div>
      <div style={{ background: `rgba(${theme.rgb},0.06)`, padding: "12px 20px" }}>
        <p className="text-base leading-relaxed whitespace-pre-wrap break-words font-body" style={{ color: theme.body }}>{m.body}</p>
        <p className="font-mono text-[10px] mt-2" style={{ color: "#6B6D70" }}>Saved {fmtTs(m.saved_at || m.created_at)} · not delivered</p>
      </div>
    </div>
  );
}

function MemberPanel({ member, theme }) {
  if (!member) return null;
  return (
    <div data-testid="mailbox-member-panel" className="px-4 py-3 mb-3" style={{ border: `1px solid ${theme.subheaderBorder}`, background: `rgba(${theme.rgb},0.10)` }}>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-2 flex items-center gap-1" style={{ color: theme.subheaderText }}>
        <UserCheck className="w-3 h-3" /> {member.is_business ? "Sender is a business/dealer" : "Sender is a member"}
      </p>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-xs" style={{ color: theme.body }}>
        <span>@{member.username}</span>
        {member.business_name && <span style={{ color: theme.senderName }}>{member.business_name}</span>}
        {member.created_at && <span>joined {fmtTs(member.created_at)}</span>}
        {member.days_waiting != null && <span>{member.days_waiting}d on platform</span>}
        <span className={member.identity_verified ? "text-olive-bright" : "text-amber-500"}>{member.identity_verified ? "ID verified" : "ID not verified"}</span>
        <Link to={`/admin/accounts?open=${member.user_id}`} data-testid="mailbox-member-open-account" className="hover:underline" style={{ color: theme.senderName }}>Open in Accounts →</Link>
      </div>
    </div>
  );
}

function ReplyComposer({ mailbox, theme, onSave }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await onSave(text); setText(""); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="p-3" style={{ background: theme.composerBg, borderTop: `1px solid ${theme.composerBorder}` }} data-testid="mailbox-composer">
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="mailbox-reply-input"
        placeholder={`Reply as ${mailbox} (not connected — saved, not sent)`}
        className="w-full outline-none px-3 py-2 text-sm resize-none transition-colors"
        style={{ background: theme.inputBg, border: `1px solid ${theme.inputBorder}`, color: theme.body }}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="font-mono text-[10px]" style={{ color: theme.subheaderText }}>Saved as a draft on {mailbox} · nothing is transmitted.</span>
        <button type="submit" disabled={busy || !text.trim()} data-testid="mailbox-save-draft-btn"
          className="font-mono uppercase text-[11px] tracking-wider px-4 py-2 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          style={{ background: theme.sendBg, border: `1px solid ${theme.sendBorder}`, color: theme.sendText }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Save Draft
        </button>
      </div>
    </form>
  );
}

export default function MailboxPane({ address, status, onCountsChanged }) {
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState("open");
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const bottomRef = useRef();
  const listRef = useRef();
  // MailboxPane is a NESTED master-detail (mailbox threads | message detail). On narrow widths
  // (phones + iPad portrait) the two columns can't sit side-by-side without crushing the detail into
  // a strip, so below lg we collapse to ONE column: the thread list, then the detail full-width with
  // a back tap — mirroring the inbox's own responsive master-detail (and how the other panes read).
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = () => setIsNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const { data } = await api.get(`/admin/mailboxes/${encodeURIComponent(address)}/threads`, { params: { status: filter } });
      setThreads(data.threads || []);
    } catch (err) { setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load threads."); }
    finally { setLoading(false); }
  }, [address, filter]);

  const loadDetail = useCallback(async (tid) => {
    try {
      const { data } = await api.get(`/admin/mailboxes/threads/${tid}`);
      setDetail(data);
      // Scroll only the thread body (see StaffChatPane) — never scrollIntoView, which jumps the page.
      setTimeout(() => { const el = listRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, 60);
      onCountsChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }, [onCountsChanged]);

  useEffect(() => { setActiveId(null); setDetail(null); loadThreads(); }, [loadThreads]);
  useEffect(() => { if (activeId) loadDetail(activeId); }, [activeId, loadDetail]);
  useEffect(() => { api.get("/admin/team/members").then(({ data }) => setMembers(data.members || [])).catch(() => {}); }, []);

  const saveDraft = async (text) => {
    try { await api.post(`/admin/mailboxes/threads/${activeId}/reply`, { body: text }); toast.success("Draft saved (not sent)."); loadDetail(activeId); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const addNote = async (text) => {
    try { await api.post(`/admin/mailboxes/threads/${activeId}/notes`, { body: text }); toast.success("Internal note added."); loadDetail(activeId); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const assign = async (assignee_id) => {
    try { await api.post(`/admin/mailboxes/threads/${activeId}/assign`, { assignee_id }); setAssignOpen(false); loadDetail(activeId); loadThreads(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const resolve = async () => {
    try {
      const { data } = await api.post(`/admin/mailboxes/threads/${activeId}/resolve`, {});
      toast.success(data.status === "resolved" ? "Thread resolved." : "Thread reopened.");
      loadDetail(activeId); loadThreads(); onCountsChanged?.();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const exportMail = async (format) => {
    try {
      const token = localStorage.getItem("os_token");
      const res = await fetch(`${API}/admin/mailboxes/export?format=${format}&mailbox=${encodeURIComponent(address)}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `mailbox_${address}.${format}`; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Export downloaded.");
    } catch { toast.error("Export failed."); }
  };

  const backToThreads = () => { setActiveId(null); setDetail(null); };

  const resolved = detail?.thread?.status === "resolved";
  // Conversation skin derives from the OTHER PARTY (mailbox threads are always EXTERNAL).
  const type = detail ? (detail.party_type || resolveConvType(detail.member)) : "member";
  const theme = convTheme(type);
  const labels = convLabels(type, { audience: "admin" });

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0" data-testid="mailbox-pane">
      <div data-testid="mailbox-not-connected-notice" className="px-4 py-2 flex items-start gap-2 border-b"
        style={{ background: "rgba(94,47,168,0.10)", borderColor: "rgba(94,47,168,0.4)" }}>
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#AE8FD9" }} />
        <p className="font-mono text-[11px] leading-relaxed" style={{ color: "#C9B6E6" }}>
          Inbound email is <b>not connected</b>. These threads are <b>sample data</b> — anything you save here is a draft only and <b>is never sent to anyone</b>.
        </p>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* thread list */}
        <div className={`min-h-0 flex-col ${isNarrow ? (activeId ? "hidden" : "flex w-full") : "flex w-[340px] shrink-0 border-r border-sniper"}`}>
          <div className="px-4 py-3 border-b border-sniper flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="font-mono text-sm text-foreground flex items-center gap-2 break-all"><Mail className="w-4 h-4 text-olive-bright shrink-0" />{address}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest mt-0.5" style={{ color: status === "send_only" ? "#8B8D90" : "#7FA648" }}>{status === "send_only" ? "● send-only · unmonitored" : "● receives"}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => exportMail("json")} data-testid="mailbox-export-json" title="Export JSON" className="border border-sniper hover:border-olive text-muted-foreground hover:text-foreground px-1.5 py-1 transition-colors"><Download className="w-3 h-3" /></button>
              <button onClick={() => exportMail("csv")} data-testid="mailbox-export-csv" title="Export CSV" className="border border-sniper hover:border-olive text-muted-foreground hover:text-foreground font-mono uppercase text-[10px] px-1.5 py-1 transition-colors">CSV</button>
            </div>
          </div>
          <div className="flex border-b border-sniper">
            {["open", "resolved", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`mailbox-filter-${f}`}
                className={`flex-1 py-2 font-mono text-[10px] uppercase tracking-wider transition-colors ${filter === f ? "text-foreground border-b-2 border-olive-bright" : "text-muted-foreground hover:text-foreground border-b-2 border-transparent"}`}>{f}</button>
            ))}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {loading && <div className="p-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
            {!loading && loadErr && <LoadFailed reason={loadErr} onRetry={loadThreads} testid="mailbox-load-failed" />}
            {!loading && !loadErr && threads.length === 0 && <p className="p-6 text-xs font-mono text-muted-foreground">No threads.</p>}
            {!loadErr && threads.map((t) => (
              <button key={t.thread_id} onClick={() => setActiveId(t.thread_id)} data-testid={`mailbox-thread-${t.thread_id}`}
                className={`w-full text-left px-4 py-3 border-b border-sniper transition-colors ${activeId === t.thread_id ? "bg-olive/15 border-l-2 border-l-olive-bright" : "hover:bg-white/5 border-l-2 border-l-transparent"}`}>
                <div className="flex items-center gap-2">
                  {t.unread && !t.is_seed && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#EBB44A" }} />}
                  <span className={`text-sm truncate ${t.unread && !t.is_seed ? "text-foreground font-medium" : "text-secondary-foreground"}`}>{t.subject}</span>
                  {t.is_seed && <span data-testid={`mailbox-sample-${t.thread_id}`} className="ml-auto font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 shrink-0" style={{ background: "rgba(94,47,168,0.2)", color: "#AE8FD9" }}>Sample</span>}
                </div>
                <p className="font-mono text-[10px] text-muted-foreground truncate mt-1 break-all">{t.sender_email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono text-[9px] text-muted-foreground">{fmtTs(t.last_at)}</span>
                  {t.status === "resolved" && <span className="font-mono text-[9px] uppercase text-olive-bright">resolved</span>}
                  {t.assignee_username && <span className="font-mono text-[9px] text-muted-foreground">→ @{t.assignee_username}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* thread detail — the CONVERSATION WINDOW, fully skinned by type */}
        <div className={`flex-col min-w-0 min-h-0 ${isNarrow ? (activeId ? "flex w-full" : "hidden") : "flex flex-1"}`} style={{ background: detail ? theme.windowBg : undefined }}>
          {isNarrow && activeId && (
            <button onClick={backToThreads} data-testid="mailbox-threads-back"
              className="flex items-center gap-2 px-3 py-2.5 border-b border-sniper text-olive-bright font-mono uppercase text-[11px] shrink-0"
              style={{ background: theme.headerBg }}>
              <ArrowLeft className="w-3.5 h-3.5" /> Threads
            </button>
          )}
          {!detail ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono uppercase text-xs">Select a thread</div>
          ) : (
            <>
              <ConversationHeader
                theme={theme} labels={labels} title={detail.thread.subject} subtitle={detail.thread.sender_email}
                testid="mailbox-detail-header"
                actions={(
                  <>
                    <div className="relative">
                      <button onClick={() => setAssignOpen((v) => !v)} data-testid="mailbox-assign-btn"
                        className="border font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors flex items-center gap-1"
                        style={{ borderColor: theme.pill.border, color: theme.headerText }}>
                        {detail.thread.assignee_username ? `@${detail.thread.assignee_username}` : "Assign"} <ChevronDown className="w-3 h-3" />
                      </button>
                      {assignOpen && (
                        <div data-testid="mailbox-assign-menu" className="absolute right-0 mt-1 w-48 bg-surface border border-sniper z-20 max-h-64 overflow-y-auto">
                          <button onClick={() => assign(null)} data-testid="mailbox-assign-none" className="w-full text-left px-3 py-2 font-mono text-xs text-muted-foreground hover:bg-white/5">Unassigned</button>
                          {members.map((mem) => (
                            <button key={mem.user_id} onClick={() => assign(mem.user_id)} data-testid={`mailbox-assign-${mem.user_id}`} className="w-full text-left px-3 py-2 font-mono text-xs text-secondary-foreground hover:bg-white/5 hover:text-foreground">@{mem.username}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={resolve} data-testid="mailbox-resolve-btn"
                      className="font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors flex items-center gap-1 border"
                      style={{ borderColor: theme.pill.border, color: theme.headerText }}>
                      {resolved ? <><RotateCcw className="w-3 h-3" /> Reopen</> : <><CheckCircle2 className="w-3 h-3" /> Resolve</>}
                    </button>
                  </>
                )}
              />

              <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-3" data-testid="mailbox-thread-body">
                <MemberPanel member={detail.member} theme={theme} />
                {detail.messages.map((m) => (
                  m.direction === "inbound"
                    ? <InboundMessage key={m.message_id} m={m} theme={theme} />
                    : <OutboundMessage key={m.message_id} m={m} theme={theme} />
                ))}
                <div ref={bottomRef} />
              </div>

              {/* internal-notes rail — SECOND composer, olive, never sent */}
              <InternalNotesRail notes={detail.notes || []} onAdd={addNote} fmtTs={fmtTs} />

              {detail.can_reply ? (
                <>
                  <SharesNotice theme={theme} recipient={detail.thread.sender_email} />
                  <ReplyComposer mailbox={detail.thread.mailbox} theme={theme} onSave={saveDraft} />
                </>
              ) : (
                <div className="p-3 border-t border-sniper font-mono text-[11px] text-muted-foreground flex items-center gap-2" data-testid="mailbox-sendonly-note">
                  <ShieldAlert className="w-3.5 h-3.5" /> {detail.thread.mailbox} is send-only / unmonitored — replies aren't handled here.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
